import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaMicrophone, FaPaperPlane, FaTimes, FaWhatsapp, FaVolumeUp, FaVolumeMute, FaPhone } from 'react-icons/fa';
import twemoji from 'twemoji';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

const TwemojiText = ({ text }) => {
  if (!text) return null;
  const cleanHtml = twemoji.parse(text, {
    folder: 'svg',
    ext: '.svg',
    base: 'https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/'
  });
  return <span dangerouslySetInnerHTML={{ __html: cleanHtml }} />;
};

const VefiChatGlobal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [showBubble, setShowBubble] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('vefi_sound_enabled') !== 'false';
  });
  const [history, setHistory] = useState([]);
  const [userCoords, setUserCoords] = useState(null);
  
  const [hasLocalAssistant, setHasLocalAssistant] = useState(!!window.vefiLocalMascotActive);
  
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: 'vefi',
      text: 'Hola 👋 Soy VEFI, tu asistente automotor.\n\nPuedo ayudarte a encontrar talleres, gomerías, repuestos, concesionarias y otros servicios registrados en Vefrek.',
      chips: [
        "🔧 Talleres",
        "🚗 Concesionarias",
        "🛠️ Gomerías",
        "🔋 Repuestos",
        "🚨 Emergencias"
      ]
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Listen to local assistant events
  useEffect(() => {
    const handleLocalActive = () => setHasLocalAssistant(true);
    const handleLocalInactive = () => setHasLocalAssistant(false);

    window.addEventListener('vefiLocalMascotActive', handleLocalActive);
    window.addEventListener('vefiLocalMascotInactive', handleLocalInactive);

    setHasLocalAssistant(!!window.vefiLocalMascotActive);

    return () => {
      window.removeEventListener('vefiLocalMascotActive', handleLocalActive);
      window.removeEventListener('vefiLocalMascotInactive', handleLocalInactive);
    };
  }, []);

  // Reset visibility when the route changes
  useEffect(() => {
    setIsVisible(true);
    window.dispatchEvent(new Event('vefiMascotOpened'));
  }, [location.pathname]);

  // Hide bubble after 5s
  useEffect(() => {
    const t = setTimeout(() => setShowBubble(false), 5000);
    return () => clearTimeout(t);
  }, []);

  const isNotAutomotive = (text) => {
    const nonAutoKeywords = [
      'receta', 'cocinar', 'ascensor', 'pizza', 'comida', 'película', 'cine',
      'electricidad domiciliaria', 'puesta a tierra', 'albañil', 'plomero', 'abogado'
    ];
    return nonAutoKeywords.some(keyword => text.toLowerCase().includes(keyword));
  };

  // Web Audio API sound synthesis
  const playOpenSound = () => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(300, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.12);
      
      gain.gain.setValueAtTime(0.04, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
      
      osc.start();
      osc.stop(ctx.currentTime + 0.12);
    } catch (e) {}
  };

  const playCloseSound = () => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(500, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(250, ctx.currentTime + 0.15);
      
      gain.gain.setValueAtTime(0.03, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
      
      osc.start();
      osc.stop(ctx.currentTime + 0.15);
    } catch (e) {}
  };

  const playMicSound = () => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(600, ctx.currentTime);
      osc.frequency.setValueAtTime(800, ctx.currentTime + 0.08);
      
      gain.gain.setValueAtTime(0.03, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
      
      osc.start();
      osc.stop(ctx.currentTime + 0.18);
    } catch (e) {}
  };

  const playSuccessSound = () => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(523.25, ctx.currentTime); // C5
      gain1.gain.setValueAtTime(0.03, ctx.currentTime);
      gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
      osc1.start();
      osc1.stop(ctx.currentTime + 0.15);
      
      setTimeout(() => {
        try {
          const osc2 = ctx.createOscillator();
          const gain2 = ctx.createGain();
          osc2.connect(gain2);
          gain2.connect(ctx.destination);
          osc2.type = 'sine';
          osc2.frequency.setValueAtTime(659.25, ctx.currentTime); // E5
          gain2.gain.setValueAtTime(0.03, ctx.currentTime);
          gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.20);
          osc2.start();
          osc2.stop(ctx.currentTime + 0.20);
        } catch (e) {}
      }, 75);
    } catch (e) {}
  };

  // Toggle sound enabled
  const toggleSound = () => {
    setSoundEnabled(prev => {
      const next = !prev;
      localStorage.setItem('vefi_sound_enabled', String(next));
      return next;
    });
  };

  // Request HTML5 location
  const requestLocation = () => {
    if (navigator.geolocation && !userCoords) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserCoords({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.warn("Location permission denied or unavailable:", error);
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 }
      );
    }
  };

  // Build smart no-results message depending on keywords
  const getContextNoResultsMsg = (query) => {
    const q = query.toLowerCase().trim();
    
    // Localidad sin resultados
    const locationKeywords = [
      'en ', 'cerca de', 'localidad', 'ciudad', 'zona', 'provincia', 'buenos aires', 'rosario', 'cordoba', 
      'santa fe', 'mendoza', 'tucuman', 'la plata', 'mar del plata', 'salta', 'bahia blanca'
    ];
    if (locationKeywords.some(k => q.includes(k))) {
      return "No encontramos empresas registradas para ese servicio en la localidad indicada.";
    }

    // Categoría inexistente
    const categoryKeywords = [
      'taller', 'talleres', 'gomeria', 'gomerias', 'repuesto', 'repuestos', 'concesionaria', 'concesionarias',
      'agencia', 'agencias', 'auxilio', 'remolque', 'mecanico', 'mecanicos', 'chapa', 'pintura', 'lavadero',
      'bateria', 'baterias', 'electricidad', 'electricista', 'alineacion', 'balanceo', 'freno', 'frenos',
      'categoria', 'rubro', 'servicio', 'servicios'
    ];
    if (categoryKeywords.some(k => q.includes(k))) {
      return "Por el momento no tenemos empresas registradas en esa categoría.";
    }

    // Empresa inexistente
    const words = q.split(/\s+/);
    if (q.includes('buscar') || q.startsWith('empresa') || q.startsWith('negocio') || words.length >= 2) {
      return "No encontramos empresas registradas con ese nombre dentro de Vefrek.";
    }

    // Sin coincidencias
    return "No encontré resultados relacionados con tu búsqueda dentro de Vefrek. Probá con otra categoría o palabra clave.";
  };

  const initSpeechRecognition = () => {
    if (!SpeechRecognition) return;
    if (!recognitionRef.current) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.lang = 'es-AR';
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputText(transcript);
        setIsListening(false);
        handleSend(transcript);
      };
      recognitionRef.current.onerror = () => setIsListening(false);
      recognitionRef.current.onend = () => setIsListening(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { initSpeechRecognition(); }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 50);
    }
  }, [messages, isOpen, isProcessing]);

  const toggleMic = () => {
    if (!SpeechRecognition) {
      alert('Tu navegador no soporta búsqueda por voz. Usá Chrome o Edge.');
      return;
    }
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      try { 
        playMicSound();
        recognitionRef.current?.start(); 
        setIsListening(true); 
      } catch (e) {}
    }
  };

  const handleSend = async (textToSend = inputText) => {
    if (!textToSend.trim()) return;

    // Save current message history before updating
    setHistory(prev => [...prev, messages]);

    // Strip emoji icons (like 🔧 🚗) from query text
    const cleanedText = textToSend.replace(/[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF]/g, '').trim();

    setMessages(prev => prev.map(m => m.chips ? { ...m, chips: null } : m));
    const userMsg = { id: Date.now(), sender: 'user', text: textToSend };
    setMessages(prev => [...prev, userMsg]);
    setInputText('');
    setIsProcessing(true);

    if (isNotAutomotive(cleanedText)) {
      setTimeout(() => {
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          sender: 'vefi',
          text: 'Soy VEFI, el asistente automotor de Vefrek. Solo puedo ayudarte con consultas relacionadas a vehículos, servicios automotores, productos y empresas registradas en Vefrek.'
        }]);
        setIsProcessing(false);
      }, 800);
      return;
    }

    try {
      const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
      const response = await axios.get(`${apiBase}/api/vefi-search`, {
        params: {
          query: cleanedText,
          lat: userCoords?.lat,
          lng: userCoords?.lng
        }
      });
      const { companysData } = response.data;
      if (companysData && companysData.length > 0) {
        playSuccessSound();
        setMessages(prev => [...prev, {
          id: Date.now() + 1, sender: 'vefi',
          text: `Encontré ${companysData.length} ${companysData.length === 1 ? 'opción' : 'opciones'} para vos:`,
          results: companysData
        }]);
      } else {
        setMessages(prev => [...prev, {
          id: Date.now() + 1, sender: 'vefi',
          text: getContextNoResultsMsg(cleanedText)
        }]);
      }
    } catch {
      setMessages(prev => [...prev, {
        id: Date.now() + 1, sender: 'vefi',
        text: 'Tuvimos un problema al conectar con el servidor de Vefrek. Por favor, intentá de nuevo en unos momentos.'
      }]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    setShowBubble(false);
    playOpenSound();
    requestLocation();
  };

  const handleClose = () => {
    setIsOpen(false);
    playCloseSound();
  };

  const handleBack = () => {
    if (history.length > 0) {
      const prevMessages = history[history.length - 1];
      setHistory(prev => prev.slice(0, -1));
      setMessages(prevMessages);
      playOpenSound(); // subtle sound
    }
  };

  // Helper to format WhatsApp phone number (Argentina country code + cell prefix helper)
  const formatPhoneForWA = (phone) => {
    if (!phone) return '';
    let clean = phone.replace(/\D/g, '');
    if (clean.length === 10) {
      clean = '549' + clean;
    }
    return clean;
  };

  // Helper to clean phone numbers for calling
  const formatPhoneForCall = (phone) => {
    if (!phone) return '';
    return phone.trim();
  };

  // Helper to print company address details
  const displayLocation = (company) => {
    const parts = [];
    if (company.location) parts.push(company.location);
    if (company.city) parts.push(company.city);
    return parts.join(', ');
  };

  if (hasLocalAssistant) {
    return null;
  }

  return (
    <>
      <style>{`
        img.emoji {
          height: 1.25em;
          width: 1.25em;
          margin: 0 0.1em;
          vertical-align: -0.2em;
          display: inline-block;
        }

        /* ─── VEFI wrapper ─────────────────────────────── */
        .vefi-root {
          position: fixed;
          bottom: 24px;
          right: 20px;
          z-index: 10000;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          pointer-events: none;
          user-select: none;
        }

        @media (max-width: 1024px) {
          .vefi-root {
            bottom: calc(24px + env(safe-area-inset-bottom, 0px));
            right: calc(20px + env(safe-area-inset-right, 0px));
          }
        }

        /* ─── Floating avatar (minimized) ─────────────── */
        .vefi-avatar-wrap {
          position: relative;
          pointer-events: all;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 8px;
        }

        .vefi-avatar {
          width: 102px;
          height: 102px;
          border-radius: 50%;
          background: radial-gradient(circle at 40% 40%, rgba(0,197,142,0.18), transparent 70%);
          display: flex;
          align-items: center;
          justify-content: center;
          animation: vefiFloat 3.5s ease-in-out infinite;
          filter: drop-shadow(0 0 10px rgba(0,197,142,0.45));
          transition: filter 0.3s, transform 0.2s;
        }

        .vefi-avatar:hover {
          filter: drop-shadow(0 0 18px rgba(0,197,142,0.75));
          transform: scale(1.05);
        }

        .vefi-avatar img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .vefi-avatar-fallback {
          font-size: 44px;
          line-height: 1;
        }

        @keyframes vefiFloat {
          0%   { transform: translateY(0px); }
          50%  { transform: translateY(-8px); }
          100% { transform: translateY(0px); }
        }

        /* ─── Close (X) button on avatar ─────────────── */
        .vefi-close-mini {
          position: absolute;
          top: -2px;
          right: -2px;
          width: 20px;
          height: 20px;
          background: #111827;
          border: 1.5px solid rgba(255,255,255,0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          color: #9ca3af;
          cursor: pointer;
          transition: background 0.2s, color 0.2s;
          pointer-events: all;
        }
        .vefi-close-mini:hover { background: #ef4444; color: white; }

        /* ─── Close button near VEFI's foot (mobile only) ─── */
        .vefi-mobile-close-btn {
          display: flex;
          position: absolute;
          bottom: 2px;
          left: 68%;
          transform: translateX(-50%);
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: rgba(17, 24, 39, 0.85);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          border: 1.5px solid rgba(255, 255, 255, 0.4);
          color: #f3f4f6;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10002;
          pointer-events: all;
          transition: background-color 0.2s, transform 0.2s, border-color 0.2s;
          padding: 0;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }
        .vefi-mobile-close-btn:hover {
          background: #ef4444;
          border-color: #ef4444;
          color: white;
          transform: translateX(-50%) scale(1.1);
        }

        /* ─── Speech bubble ───────────────────────────── */
        .vefi-bubble {
          background: rgba(17, 24, 39, 0.92);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(0, 197, 142, 0.3);
          color: #f3f4f6;
          font-size: 12.5px;
          font-family: 'Inter', sans-serif;
          line-height: 1.4;
          padding: 9px 13px;
          border-radius: 12px;
          border-bottom-right-radius: 2px;
          max-width: 210px;
          white-space: normal;
          pointer-events: all;
          cursor: pointer;
          animation: bubbleIn 0.35s cubic-bezier(0.16,1,0.3,1) forwards;
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
          margin-bottom: 8px;
        }

        .vefi-bubble span {
          color: #00c58e;
          font-weight: 600;
        }

        @keyframes bubbleIn {
          from { opacity: 0; transform: translateX(12px) scale(0.93); }
          to   { opacity: 1; transform: translateX(0) scale(1); }
        }

        /* ─── CHAT WINDOW ─────────────────────────────── */
        .vefi-chat-window {
          position: fixed;
          bottom: 24px;
          right: 20px;
          width: 390px;
          max-width: calc(100vw - 24px);
          height: 560px;
          max-height: calc(100dvh - 50px);
          background: #ffffff;
          border-radius: 18px;
          box-shadow: 0 16px 48px rgba(0,0,0,0.22), 0 0 0 1px rgba(0,197,142,0.12);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          z-index: 10000;
          animation: chatSlideUp 0.32s cubic-bezier(0.16,1,0.3,1) forwards;
          pointer-events: all;
        }

        @media (max-width: 500px) {
          .vefi-chat-window {
            right: 12px;
            width: calc(100vw - 24px);
            bottom: calc(16px + env(safe-area-inset-bottom, 0px));
            height: calc(100dvh - 40px);
            max-height: calc(100dvh - 40px);
          }
        }

        @keyframes chatSlideUp {
          from { opacity: 0; transform: translateY(24px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }

        /* HEADER */
        .vefi-chat-header {
          background: linear-gradient(135deg, #00c58e 0%, #00a375 100%);
          padding: 14px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
        }

        .vefi-chat-header-left { display: flex; align-items: center; gap: 8px; }
        .vefi-chat-header-info { display: flex; align-items: center; gap: 8px; }

        .vefi-back-btn {
          background: rgba(255, 255, 255, 0.16);
          border: none;
          border-radius: 6px;
          padding: 4px 8px;
          font-size: 11.5px;
          font-weight: 600;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          transition: background 0.2s;
        }
        .vefi-back-btn:hover { background: rgba(255, 255, 255, 0.3); }

        .vefi-header-avatar {
          width: 42px; height: 42px;
          background: rgba(255,255,255,0.2);
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          overflow: hidden;
          border: 1.5px solid rgba(255,255,255,0.35);
        }
        .vefi-header-avatar img { width: 100%; height: 100%; object-fit: contain; }

        .vefi-header-title { color: white; font-size: 15.5px; font-weight: 700; line-height: 1.2; }
        .vefi-header-sub { color: rgba(255,255,255,0.85); font-size: 12px; }

        .vefi-chat-header-actions { display: flex; align-items: center; gap: 8px; }

        .vefi-sound-toggle-btn {
          background: rgba(255,255,255,0.15);
          border: none;
          border-radius: 50%;
          width: 28px; height: 28px;
          display: flex; align-items: center; justify-content: center;
          color: white;
          cursor: pointer;
          transition: background 0.2s;
          flex-shrink: 0;
        }
        .vefi-sound-toggle-btn:hover { background: rgba(255,255,255,0.3); }

        .vefi-chat-close-btn {
          background: rgba(255,255,255,0.15);
          border: none;
          border-radius: 50%;
          width: 28px; height: 28px;
          display: flex; align-items: center; justify-content: center;
          color: white;
          cursor: pointer;
          transition: background 0.2s;
          flex-shrink: 0;
        }
        .vefi-chat-close-btn:hover { background: rgba(255,255,255,0.3); }

        /* MESSAGES */
        .vefi-chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 14px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          background: #f8fafc;
          scroll-behavior: smooth;
        }

        .vefi-chat-messages::-webkit-scrollbar { width: 5px; }
        .vefi-chat-messages::-webkit-scrollbar-track { background: transparent; }
        .vefi-chat-messages::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

        .vefi-msg-row { display: flex; align-items: flex-end; gap: 6px; }
        .vefi-msg-row.user { flex-direction: row-reverse; }

        .vefi-msg-avatar-sm {
          width: 32px; height: 32px; border-radius: 50%;
          background: #e6f9f3;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          overflow: hidden;
        }
        .vefi-msg-avatar-sm img { width: 100%; height: 100%; object-fit: contain; }

        .vefi-msg-bubble {
          max-width: 82%;
          padding: 11px 15px;
          border-radius: 14px;
          font-size: 14px;
          line-height: 1.5;
          word-wrap: break-word;
          overflow-wrap: break-word;
          word-break: break-word;
          white-space: pre-wrap;
        }
        .vefi-msg-bubble.bot {
          background: white;
          color: #1e293b;
          border-bottom-left-radius: 4px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 4px rgba(0,0,0,0.04);
        }
        .vefi-msg-bubble.user {
          background: #111827;
          color: white;
          border-bottom-right-radius: 4px;
        }

        /* CHIPS */
        .vefi-chips-wrap { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; max-width: 90%; }
        .vefi-chip-btn {
          background: #ffffff;
          border: 1.5px solid rgba(0,197,142,0.4);
          color: #00a375;
          padding: 6px 13px;
          border-radius: 100px;
          font-size: 12.5px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.18s;
          white-space: nowrap;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .vefi-chip-btn:hover { background: #00c58e; color: white; border-color: #00c58e; }

        /* RESULT CARDS */
        .vefi-result-card {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-left: 3.5px solid #00c58e;
          border-radius: 10px;
          padding: 12px 14px;
          margin-top: 6px;
          box-shadow: 0 1.5px 5px rgba(0,0,0,0.05);
          width: 100%;
          max-width: 300px;
        }
        .vefi-result-card h5 { 
          margin: 0 0 4px; 
          font-size: 14.5px; 
          font-weight: 700; 
          color: #1e293b;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        
        .vefi-result-meta {
          display: flex;
          flex-direction: column;
          gap: 3.5px;
          margin-bottom: 9px;
          font-size: 12px;
          color: #64748b;
        }
        
        .vefi-result-category {
          font-weight: 700;
          color: #00a375;
          text-transform: uppercase;
          font-size: 10px;
          letter-spacing: 0.5px;
        }
        
        .vefi-result-location, .vefi-result-phone, .vefi-result-distance {
          display: flex;
          align-items: center;
          gap: 5px;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }

        .vefi-result-actions { display: flex; gap: 6px; flex-wrap: wrap; }
        .vefi-btn-sm {
          border: none; border-radius: 6px; padding: 6px 11px;
          font-size: 11.5px; font-weight: 700; cursor: pointer;
          transition: opacity 0.2s; display: flex; align-items: center; gap: 4px;
          text-decoration: none;
        }
        .vefi-btn-sm:hover { opacity: 0.85; }
        .vefi-btn-sm.primary { background: #111827; color: white; }
        .vefi-btn-sm.wa      { background: #25d366; color: white; }
        .vefi-btn-sm.call    { background: #3b82f6; color: white; }

        /* TYPING INDICATOR */
        .vefi-typing { display: flex; gap: 4px; align-items: center; padding: 2px 0; }
        .vefi-typing-dot {
          width: 6px; height: 6px; background: #94a3b8;
          border-radius: 50%;
          animation: typingBounce 1.2s infinite ease-in-out both;
        }
        .vefi-typing-dot:nth-child(1) { animation-delay: -0.32s; }
        .vefi-typing-dot:nth-child(2) { animation-delay: -0.16s; }
        @keyframes typingBounce {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
          40%  { transform: scale(1); opacity: 1; }
        }

        /* INPUT */
        .vefi-chat-input-area {
          padding: 10px 12px;
          background: white;
          border-top: 1px solid #f1f5f9;
          display: flex;
          align-items: center;
          gap: 7px;
          flex-shrink: 0;
        }
        .vefi-text-input {
          flex: 1;
          background: #f1f5f9;
          border: none;
          border-radius: 20px;
          padding: 9px 14px;
          font-size: 13.5px;
          outline: none;
          color: #1e293b;
          transition: background 0.2s;
        }
        .vefi-text-input:focus { background: #e6f9f3; }
        .vefi-text-input::placeholder { color: #94a3b8; }
        .vefi-mic-btn {
          width: 34px; height: 34px; border-radius: 50%;
          border: none; background: #f1f5f9; color: #64748b;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.2s; flex-shrink: 0;
        }
        .vefi-mic-btn:hover { background: #e2e8f0; color: #1e293b; }
        .vefi-mic-btn.listening {
          background: #fee2e2; color: #ef4444;
          animation: micPulse 1.5s infinite;
        }
        @keyframes micPulse {
          0%   { box-shadow: 0 0 0 0 rgba(239,68,68,0.4); }
          70%  { box-shadow: 0 0 0 7px rgba(239,68,68,0); }
          100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); }
        }
        .vefi-send-btn {
          width: 34px; height: 34px; border-radius: 50%;
          border: none; background: #00c58e; color: white;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.2s; flex-shrink: 0;
        }
        .vefi-send-btn:hover  { background: #00a375; }
        .vefi-send-btn:disabled { opacity: 0.45; cursor: not-allowed; }
      `}</style>

      {/* ── MINIMIZED: floating VEFI avatar ── */}
      {!isOpen && isVisible && (
        <div className="vefi-root">
          {/* Speech bubble */}
          {showBubble && (
            <div className="vefi-bubble" onClick={handleOpen}>
              ¿Buscás algo para tu auto? <span>Preguntame</span> <TwemojiText text="🚗" />
            </div>
          )}

          {/* Avatar */}
          <div className="vefi-avatar-wrap">
            <div className="vefi-avatar" onClick={handleOpen}>
              <img
                src="/images/logos/vefi.png"
                alt="VEFI"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <span className="vefi-avatar-fallback" style={{ display: 'none' }}><TwemojiText text="🚗" /></span>
            </div>

            {/* Small close button near the foot of VEFI on mobile */}
            <button
              className="vefi-mobile-close-btn"
              onClick={(e) => {
                e.stopPropagation();
                setIsVisible(false);
                window.dispatchEvent(new Event('vefiMascotClosed'));
              }}
              title="Cerrar VEFI"
            >
              <FaTimes size={10} />
            </button>
          </div>
        </div>
      )}

      {/* ── EXPANDED: full chat window ── */}
      {isOpen && (
        <div className="vefi-chat-window">
          {/* Header */}
          <div className="vefi-chat-header">
            <div className="vefi-chat-header-left">
              {history.length > 0 && (
                <button className="vefi-back-btn" onClick={handleBack}>
                  ← Volver
                </button>
              )}
              <div className="vefi-chat-header-info">
                <div className="vefi-header-avatar">
                  <img src="/images/logos/vefi.png" alt="VEFI" onError={(e) => { e.target.style.display='none'; }} />
                </div>
                <div>
                  <div className="vefi-header-title">VEFI</div>
                  <div className="vefi-header-sub">Asistente Automotor</div>
                </div>
              </div>
            </div>
            
            <div className="vefi-chat-header-actions">
              <button 
                className="vefi-sound-toggle-btn" 
                onClick={toggleSound}
                title={soundEnabled ? "Silenciar sonidos" : "Activar sonidos"}
              >
                {soundEnabled ? <FaVolumeUp size={13} /> : <FaVolumeMute size={13} />}
              </button>
              <button className="vefi-chat-close-btn" onClick={handleClose}>
                <FaTimes size={13} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="vefi-chat-messages">
            {messages.map((msg) => (
              <React.Fragment key={msg.id}>
                <div className={`vefi-msg-row ${msg.sender === 'user' ? 'user' : ''}`}>
                  {msg.sender === 'vefi' && (
                    <div className="vefi-msg-avatar-sm">
                      <img src="/images/logos/vefi.png" alt="VEFI" onError={(e) => { e.target.style.display='none'; }} />
                    </div>
                  )}
                  <div className={`vefi-msg-bubble ${msg.sender === 'vefi' ? 'bot' : 'user'}`}>
                    <TwemojiText text={msg.text} />
                  </div>
                </div>

                {/* Chips */}
                {msg.chips && (
                  <div className="vefi-chips-wrap" style={{ marginLeft: '32px' }}>
                    {msg.chips.map((chip, idx) => (
                      <button key={idx} className="vefi-chip-btn" onClick={() => handleSend(chip)}>
                        <TwemojiText text={chip} />
                      </button>
                    ))}
                  </div>
                )}

                {/* Result cards */}
                {msg.results && (
                  <div style={{ marginLeft: '32px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {msg.results.map((company, idx) => (
                      <div key={company._id || idx} className="vefi-result-card">
                        <h5>{company.name}</h5>
                        
                        <div className="vefi-result-meta">
                          <span className="vefi-result-category">{company.category}</span>
                          
                          {displayLocation(company) && (
                            <span className="vefi-result-location">
                              <TwemojiText text={`📍 ${displayLocation(company)}`} />
                            </span>
                          )}
                          
                          {company.phone && (
                            <span className="vefi-result-phone">
                              <TwemojiText text={`📞 ${company.phone}`} />
                            </span>
                          )}

                          {company.distance !== undefined && company.distance !== null && (
                            <span className="vefi-result-distance">
                              <TwemojiText text={`🚗 a ${company.distance.toFixed(1)} km`} />
                            </span>
                          )}
                        </div>

                        <div className="vefi-result-actions">
                          <button className="vefi-btn-sm primary" onClick={() => navigate(`/${company.slug}`)}>
                            Ver perfil
                          </button>
                          
                          {(company.social?.whatsapp || company.phone) && (
                            <a
                              href={`https://wa.me/${formatPhoneForWA(company.social?.whatsapp || company.phone)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="vefi-btn-sm wa"
                            >
                              <FaWhatsapp size={12} /> WhatsApp
                            </a>
                          )}

                          {company.phone && (
                            <a
                              href={`tel:${formatPhoneForCall(company.phone)}`}
                              className="vefi-btn-sm call"
                            >
                              <FaPhone size={10} /> Llamar
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </React.Fragment>
            ))}

            {/* Typing indicator */}
            {isProcessing && (
              <div className="vefi-msg-row">
                <div className="vefi-msg-avatar-sm">
                  <img src="/images/logos/vefi.png" alt="VEFI" onError={(e) => { e.target.style.display='none'; }} />
                </div>
                <div className="vefi-msg-bubble bot">
                  <span style={{ fontSize: '11px', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                    VEFI está buscando...
                  </span>
                  <div className="vefi-typing">
                    <div className="vefi-typing-dot" />
                    <div className="vefi-typing-dot" />
                    <div className="vefi-typing-dot" />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="vefi-chat-input-area">
            <button
              className={`vefi-mic-btn ${isListening ? 'listening' : ''}`}
              onClick={toggleMic}
              title="Buscar por voz"
            >
              <FaMicrophone size={14} />
            </button>
            <input
              type="text"
              className="vefi-text-input"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder={isListening ? '🎤 Escuchando...' : 'Escribí tu consulta...'}
              disabled={isProcessing || isListening}
            />
            <button
              className="vefi-send-btn"
              onClick={() => handleSend()}
              disabled={!inputText.trim() || isProcessing}
            >
              <FaPaperPlane size={13} style={{ marginLeft: '2px' }} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default VefiChatGlobal;
